import React, { useState, useEffect, useCallback } from 'react';
import useDrawingMaterials from 'components/quote_builder_widget/hooks/useDrawingMaterials';

const BaseDrawingMaterialChooser = ({
  widgetIdentifier,
  drawingId,
  onMaterialChange,
  onCartonMaterialSelected,
  styles,
  selectClassName
}) => {
  const { materials, loading } = useDrawingMaterials(
    widgetIdentifier,
    drawingId,
    null // Don't use hook's onChange, we'll manage it ourselves
  );

  // Group materials by type
  const cartonMaterials = materials.filter(m => m.type === 'carton' || !m.type);
  const corrugatedCardboardMaterials = materials.filter(m => m.type === 'corrugated_cardboard');

  // Track selected IDs for each material type
  const [selectedCartonId, setSelectedCartonId] = useState(null);
  const [selectedCorrugatedCardboardId, setSelectedCorrugatedCardboardId] = useState(null);

  // Initialize selected IDs when materials change
  useEffect(() => {
    if (cartonMaterials.length > 0) {
      setSelectedCartonId((currentId) => {
        // Only set if not already set or if current selection is no longer available
        if (!currentId || !cartonMaterials.find(m => m.id === currentId)) {
          return cartonMaterials[0].id;
        }
        return currentId;
      });
    } else {
      setSelectedCartonId(null);
    }
  }, [cartonMaterials.length, cartonMaterials.map(m => m.id).join(',')]);

  useEffect(() => {
    if (corrugatedCardboardMaterials.length > 0) {
      setSelectedCorrugatedCardboardId((currentId) => {
        // Only set if not already set or if current selection is no longer available
        if (!currentId || !corrugatedCardboardMaterials.find(m => m.id === currentId)) {
          return corrugatedCardboardMaterials[0].id;
        }
        return currentId;
      });
    } else {
      setSelectedCorrugatedCardboardId(null);
    }
  }, [corrugatedCardboardMaterials.length, corrugatedCardboardMaterials.map(m => m.id).join(',')]);

  // The carton material on show, first one included, so the printing colours follow it.
  useEffect(() => {
    if (onCartonMaterialSelected && selectedCartonId) { onCartonMaterialSelected(selectedCartonId); }
  }, [selectedCartonId]);

  const handleCartonChange = useCallback((event) => {
    const newId = event.target.value;
    setSelectedCartonId(newId);
    if (onMaterialChange) {
      onMaterialChange({ id: newId, type: 'carton' });
    }
  }, [onMaterialChange]);

  const handleCorrugatedCardboardChange = useCallback((event) => {
    const newId = event.target.value;
    setSelectedCorrugatedCardboardId(newId);
    if (onMaterialChange) {
      onMaterialChange({ id: newId, type: 'corrugated_cardboard' });
    }
  }, [onMaterialChange]);

  const shouldRender = !loading && materials.length > 0;

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      {cartonMaterials.length > 0 && (
        <div className={styles.formGroup} data-widget-element="form-group">
          <label className={styles.label} data-widget-element="label">
            {t('quote_builder.carton_material')}
          </label>
          <div className={styles.inputWrapper}>
            <select
              name="carton_material_user_price_id"
              value={selectedCartonId || ''}
              onChange={handleCartonChange}
              className={selectClassName}
              data-widget-element="select"
            >
              {cartonMaterials.map((material) => (
                <option key={material.id} value={material.id}>
                  {material.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {corrugatedCardboardMaterials.length > 0 && (
        <div className={styles.formGroup} data-widget-element="form-group">
          <label className={styles.label} data-widget-element="label">
            {t('quote_builder.corrugated_cardboard_material')}
          </label>
          <div className={styles.inputWrapper}>
            <select
              name="corrugated_cardboard_material_user_price_id"
              value={selectedCorrugatedCardboardId || ''}
              onChange={handleCorrugatedCardboardChange}
              className={selectClassName}
              data-widget-element="select"
            >
              {corrugatedCardboardMaterials.map((material) => (
                <option key={material.id} value={material.id}>
                  {material.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </>
  );
};

export default BaseDrawingMaterialChooser;
